import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CircularProgress
} from "@mui/material";
import { keyframes } from "@mui/system";
import axios from "../api/baseurl";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";

export default function AlreadyPushedModal({ open, onClose, pushId, refreshUsers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && pushId) {
      fetchData();
    }
  }, [open, pushId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      const resp = await axios.get(`/api/notifications/${pushId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const decrypted = decryptBody(resp.data, secretKey);
      setData(decrypted);
    } catch (error) {
      console.error("Failed to load pushed doctor details", error);
    } finally {
      setLoading(false);
    }
  };

  // SPINNER ANIMATION
  const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;

  const dotPositions = [
    { top: "5%", left: "50%" },
    { top: "20%", left: "80%" },
    { top: "50%", left: "95%" },
    { top: "80%", left: "80%" },
    { top: "95%", left: "50%" },
    { top: "80%", left: "20%" },
    { top: "50%", left: "5%" },
    { top: "20%", left: "20%" },
  ];

  const handleClose = () => {
    if (refreshUsers) refreshUsers();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          background: "#f8fbff"
        }
      }}
    >

      {/* HEADER */}
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1.25rem",
          color: "#1A237E",
          background: "#e8f3fb",
          borderBottom: "1px solid #d6e8f5",
          textAlign: "center"
        }}
      >
        Already Pushed Doctor Details
      </DialogTitle>

      {/* LOADING OVERLAY */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 2000
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 80,
              height: 80,
              animation: `${spinAnimation} 1.5s linear infinite`
            }}
          >
            {dotPositions.map((pos, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "#1A237E",
                  position: "absolute",
                  ...pos
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* CONTENT */}
      <DialogContent
        dividers
        sx={{
          padding: 3,
          background: "#f8fbff"
        }}
      >
        {!loading && (
          <>
            {data ? (
              <Grid container spacing={3}>

                {/* Rendering Doctor */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      padding: 2.5,
                      borderRadius: 3,
                      boxShadow: "0 3px 10px rgba(0,0,0,0.06)"
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "#1A237E",
                        fontWeight: 600,
                        marginBottom: 1
                      }}
                    >
                      Rendering Doctor
                    </Typography>

                    <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>
                      {data.renderingDoctor.firstName} {data.renderingDoctor.lastName}
                    </Typography>
                  </Card>
                </Grid>

                {/* Duty Doctors */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      padding: 2.5,
                      borderRadius: 3,
                      boxShadow: "0 3px 10px rgba(0,0,0,0.06)"
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "#1A237E",
                        fontWeight: 600,
                        marginBottom: 1
                      }}
                    >
                      Duty Doctors
                    </Typography>

                    {data.dutyDoctors?.length > 0 ? (
                      data.dutyDoctors.map((d) => (
                        <Typography
                          key={d.doctorId}
                          sx={{
                            paddingY: 0.5,
                            fontSize: "1rem"
                          }}
                        >
                          {d.firstName} {d.lastName}
                        </Typography>
                      ))
                    ) : (
                      <Typography>No duty doctors found.</Typography>
                    )}
                  </Card>
                </Grid>

              </Grid>
            ) : (
              <Typography>No Data Available</Typography>
            )}
          </>
        )}
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions sx={{ padding: 2, justifyContent: "center" }}>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#1A237E",
              color: "#fff",
            paddingX: 4,
            paddingY: 1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#1A237E"
            }
          }}
          onClick={handleClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
